import {
  Card,
  TableBody,
  TableHead,
  useTheme,
  Typography,
  Box,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Countries } from 'shared/enums';
import { CheckboxOnChangeEvent, TableRowOnClickEvent } from 'shared/types';
import { useCountriesDataStore } from 'store/countriesDataStore';
import { shallow } from 'zustand/shallow';
import Table from '@mui/material/Table';
import TableFooter from './components/Footer/Footer';
import useFooterData from './hooks/useFooterData';
import { renderColumns } from './renderers/columnsRenderer';
import { renderRows } from './renderers/rowsRenderer';
import { TableProps } from './types';
import { getRowIdsByPriority } from './utils';
import TotalOOHRow from './TotalOOHRow/TotalOOHRow';
import usePlanStore from '../../../../../../../../../store/planStore';

function EstimatedOOHBudgetTable({
  columns,
  rows,
  country = Countries.POLAND,
  currency = 'PLN',
  exchangeRate,
}: TableProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { countryForms, selectedCountry, updateCalculatedOOHBudget } =
    usePlanStore();

  const optionalRows = getRowIdsByPriority(rows, false);
  const shouldDisableCheckAll = isEmpty(optionalRows);

  const optionalIds = useCountriesDataStore(
    (state) => state[country].optionalIds,
    shallow
  );
  const updateOptionalIds = useCountriesDataStore(
    (state) => state[country].updateOptionalIds,
    shallow
  );

  const footerData = useFooterData({
    rows,
    optionalIds,
    currency,
    country,
  });

  const oohBudget =
    countryForms[selectedCountry!].offline?.ooh?.calculatedBudget;

  const handleRowClick = useCallback(
    (e: CheckboxOnChangeEvent | TableRowOnClickEvent, rowId: string) => {
      e.preventDefault();

      const newOptionalIds = {
        ...optionalIds,
        [rowId]: !optionalIds[rowId],
      };

      updateOptionalIds(newOptionalIds);

      if (!oohBudget?.data) return;

      updateCalculatedOOHBudget(selectedCountry!, {
        ...oohBudget,
        data: oohBudget.data.map((row) => ({
          ...row,
          isIncludedInCalculations:
            newOptionalIds[row.format + row.variant] ?? row.isRequired,
        })),
      });
    },
    [
      optionalIds,
      updateOptionalIds,
      selectedCountry,
      oohBudget,
      updateCalculatedOOHBudget,
    ]
  );

  const handleToggleAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newOptionalIds = event.target.checked
        ? getRowIdsByPriority(rows, false, true)
        : getRowIdsByPriority(rows, false, false);

      updateOptionalIds(newOptionalIds);
    },
    [rows, updateOptionalIds]
  );

  const renderedColumns = useMemo(
    () =>
      renderColumns({
        handleToggleAll,
        shouldDisableCheckAll,
        columns,
      }),
    [columns, handleToggleAll, shouldDisableCheckAll]
  );

  const renderedRows = useMemo(
    () =>
      renderRows({
        theme,
        optionalIds,
        handleRowClick,
        rows,
        columns,
      }),
    [theme, optionalIds, handleRowClick, rows, columns]
  );

  return (
    <Box sx={{ mt: '48px' }}>
      <Typography
        variant="body2"
        sx={{
          pl: '16px',
          pb: '12px',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}
      >
        {t('mediaPlanning.ooh.table.header')}
      </Typography>
      <Card>
        <Table>
          <TableHead>{renderedColumns}</TableHead>
          <TableBody>{renderedRows}</TableBody>
          <TotalOOHRow data={rows} />
          <TableFooter
            data={footerData}
            currency={currency}
            exchangeRate={exchangeRate}
          />
        </Table>
      </Card>
    </Box>
  );
}

export default EstimatedOOHBudgetTable;
